import { useCallback, useState } from 'react';

import { useZIndex } from '#components/cad-renderer/context/z-index-context.js';
import { ModelModuleChipContextMenu } from '#components/model-module-chip/context-menu/model-module-chip-context-menu.js';
import { useModuleSchematicInstances, useProjectModule } from '#components/model-module-chip/use-project-module.js';
import { SmartPopover } from '#components/smart-popover.js';
import { CadRendererSetActiveDesignEvent, CadRendererZoomEvent, dispatchEventAsync } from '#utils/events.js';

/**
 * Vertical offset (px) between the pinned position and the popover, matching the hover tooltip's
 * cursor offset so pinning reads as the tooltip morphing in place.
 */
export const MODULE_CLICK_POPOVER_OFFSET_PX = 8;

export interface ModuleClickPopoverProps {
	/** The placement's reference designator (instance-level for multi-unit parts). */
	moduleReferenceDesignator: string;
	/** Viewport coordinates the popover is pinned to; the popover follows when they change. */
	position: { x: number; y: number };
	/** Called when the popover should close; forwards the closing event when one exists. */
	onClose: (event?: MouseEvent | TouchEvent | React.KeyboardEvent) => void;
}

/**
 * The module info popover, pinned to the given viewport position and following it as it moves.
 * Interactive, opens without a transition, and dismisses on outside click or Escape.
 */
export function ModuleClickPopover({ moduleReferenceDesignator, position, onClose }: ModuleClickPopoverProps) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const zIndex = useZIndex('context-menu');

	const projectModule = useProjectModule(moduleReferenceDesignator);
	const moduleInstances = useModuleSchematicInstances(projectModule);

	const handleZoomToItem = useCallback(
		async (referenceDesignator: string) => {
			onClose();
			await dispatchEventAsync(new CadRendererSetActiveDesignEvent({ moduleReferenceDesignator: referenceDesignator }));
			await dispatchEventAsync(new CadRendererZoomEvent({ moduleReferenceDesignator: referenceDesignator }));
		},
		[onClose],
	);

	return (
		<>
			<div
				ref={setAnchorEl}
				className='pointer-events-none fixed'
				style={{ left: position.x, top: position.y + MODULE_CLICK_POPOVER_OFFSET_PX }}
			/>
			<SmartPopover
				open={anchorEl !== null}
				anchorEl={anchorEl}
				onClose={onClose}
				directionMargin={350}
				transitionDuration={0}
				style={{ zIndex }}
			>
				<ModelModuleChipContextMenu
					moduleInstanceName={moduleReferenceDesignator}
					projectModule={projectModule}
					onZoomToItem={handleZoomToItem}
					onClose={onClose}
					relevantInstances={moduleInstances}
				/>
			</SmartPopover>
		</>
	);
}
